import React, {useCallback, useEffect, useState} from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import CBCDialog from 'shared/components/ConfirmationDialog/CBCDialog';
import Footer, {
    CloseEventType,
} from 'shared/components/ConfirmationDialog/Footer';

// Scoped to this dialog only via the marker class below, so CBCDialog and
// Footer (both shared with other modals) stay untouched for other consumers.
const UnsavedChangesDialogGlobalStyle = createGlobalStyle`
    .modal-content:has(.unsaved-changes-dialog-marker) {
        .modal-header {
            background: #fff;
        }

        .modal-header > div {
            color: #000;
        }
    }
`;

const MessageContent = styled.div`
    width: 100%;
    padding: 20px 10px;
    text-align: center;
`;

const StyledFooter = styled(Footer)`
    background: #fff !important;

    .job-button {
        border: 1px solid transparent;
        text-decoration: none !important;

        &:hover {
            border-color: white;
            filter: brightness(0.92);
            text-decoration: none !important;
        }
    }
`;

export interface DialogOverrides {
    title?: string;
    message?: string;
    CancelButtonText?: string;
    ConfirmButtonText?: string;
    SaveButtonText?: string;
}

export interface UnsavedChangesDialogOptions {
    onCancel?: () => void | Promise<void>;
    onConfirmDiscard: () => void | Promise<void>;
    onConfirmSave?: () => void | Promise<void>;
    dialogOverrides?: Partial<DialogOverrides>;
    defaultAction?: CloseEventType;
}

export const UnsavedChangesDialogDefaults = {
    TITLE_TEXT: 'Changes not yet saved!',
    MESSAGE_TEXT: 'If you leave without saving, your changes will be lost.',
    CANCEL_BTN_TEXT: 'Keep Editing',
    CONFIRM_BTN_TEXT: 'Discard',
    SAVE_BTN_TEXT: 'Save',
};

export const useUnsavedChangesDialog = () => {
    const [show, setShow] = useState(false);
    const [options, setOptions] = useState<UnsavedChangesDialogOptions | null>(
        null
    );

    const hideDialog = useCallback(() => {
        setShow(false);
    }, []);

    const showDialog = useCallback((options: UnsavedChangesDialogOptions) => {
        setOptions(options);
        setShow(true);
    }, []);

    const handleClose = useCallback(
        (event: CloseEventType) => {
            setShow(false);

            switch (event) {
                case CloseEventType.CANCEL:
                    void options?.onCancel?.();
                    break;
                case CloseEventType.OK:
                    void options?.onConfirmDiscard();
                    break;
                case CloseEventType.APPLY:
                    void options?.onConfirmSave?.();
                    break;
            }
        },
        [options]
    );

    // Action activated by the Enter key: prefer Save, then Discard, then Cancel.
    const defaultAction =
        options?.defaultAction ??
        (options?.onConfirmSave
            ? CloseEventType.APPLY
            : options?.onConfirmDiscard
              ? CloseEventType.OK
              : CloseEventType.CANCEL);

    useEffect(() => {
        if (!show) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                handleClose(CloseEventType.CANCEL);
            } else if (event.key === 'Enter') {
                event.preventDefault();
                handleClose(defaultAction);
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [show, handleClose, defaultAction]);

    const {
        title = UnsavedChangesDialogDefaults.TITLE_TEXT,
        message = UnsavedChangesDialogDefaults.MESSAGE_TEXT,
        CancelButtonText = UnsavedChangesDialogDefaults.CANCEL_BTN_TEXT,
        ConfirmButtonText = UnsavedChangesDialogDefaults.CONFIRM_BTN_TEXT,
        SaveButtonText = UnsavedChangesDialogDefaults.SAVE_BTN_TEXT,
    } = options?.dialogOverrides || {};

    const dialog = (
        <>
            <UnsavedChangesDialogGlobalStyle />
            <CBCDialog
                display={show}
                title={title}
                width="30vw"
                footer={
                    <StyledFooter
                        onClose={handleClose}
                        noIcon="Button-Edit-Gray.svg"
                        noButtonText={CancelButtonText}
                        okButtonText={ConfirmButtonText}
                        okIcon="Options-Delete-White.svg"
                        okButtonClassname="job-button button-danger"
                        yesButtonText={
                            options?.onConfirmSave ? SaveButtonText : undefined
                        }
                    />
                }>
                <MessageContent className="unsaved-changes-dialog-marker">
                    {parseHtmlString(message)}
                </MessageContent>
            </CBCDialog>
        </>
    );

    return {
        show,
        dialog,
        showDialog,
        hideDialog,
    };
};
